import { useMemo } from "react";
import dayjs, { Dayjs } from "../utils/dayjsConfig";
import { useAppSelector } from "../store/hooks";
import { isDev } from "../utils/index";
import { userCanOperate } from "../connectors/operationApi";
import { useQuery } from "@tanstack/react-query";

// ============================================
// TEST VARIABLES FOR DEVELOPMENT
// ============================================
// Set to true to enable all market operations at any time (false = default value)
const ENABLE_MARKET_ALWAYS = false;

// Enable/disable test variables (false = use store value)
const ENABLE_TEST_VARIABLES = false;

// Force isHolidayToday (null = use store value)
const TEST_IS_HOLIDAY_TODAY: boolean | null = null;

// Force marketCanOperateT1Today (null = use store value)
const TEST_MARKET_CAN_OPERATE_T1_TODAY: boolean | null = null;

// Force outside market hours (null = calculate automatically)
const TEST_IS_OUTSIDE_MARKET_HOURS: boolean | null = null;

// Enable/disable T0 settlement operations (null = use store value)
const ENABLE_T0_SETTLEMENT = null;

// Enable/disable T1 settlement operations (null = use store value)
const ENABLE_T1_SETTLEMENT = null;
// ============================================

const ARGENTINA_TZ = "America/Argentina/Buenos_Aires";
const MARKET_OPEN_HOUR = 10;
const MARKET_OPEN_MINUTE = 30;
const MARKET_CLOSE_HOUR = 17;
const MARKET_CLOSE_MINUTE = 0;

const WEEKDAY_SATURDAY = 6;
const WEEKDAY_SUNDAY = 0;

interface UseOperationTimeProps {
  symbolType: string;
}

const isWeekend = (date: Dayjs): boolean => {
  const day = date.day();
  return day === WEEKDAY_SATURDAY || day === WEEKDAY_SUNDAY;
};

const isWithinMarketHours = (now: Dayjs): boolean => {
  const marketOpen = now
    .hour(MARKET_OPEN_HOUR)
    .minute(MARKET_OPEN_MINUTE)
    .second(0)
    .millisecond(0);
  const marketClose = now
    .hour(MARKET_CLOSE_HOUR)
    .minute(MARKET_CLOSE_MINUTE)
    .second(0)
    .millisecond(0);

  return now.isSameOrAfter(marketOpen) && now.isBefore(marketClose);
};

export const useOperationTime = ({ symbolType }: UseOperationTimeProps) => {
  const { marketCanOperateT1Today, isHolidayToday } = useAppSelector(
    (state) => state.marketStatus,
  );

  const { accountSelected } = useAppSelector((state) => state.userAccounts);

  const { data: userCanOperateData, isLoading: isLoadingUserCanOperate } =
    useQuery({
      queryKey: ["userCanOperate", accountSelected?.number],
      queryFn: () =>
        userCanOperate({ accountId: accountSelected?.number || "" }),
      enabled: !!accountSelected?.number,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    });

  // Apply test variables if enabled and in dev environment
  const finalIsHolidayToday =
    isDev && ENABLE_TEST_VARIABLES && TEST_IS_HOLIDAY_TODAY !== null
      ? TEST_IS_HOLIDAY_TODAY
      : isHolidayToday;

  const finalMarketCanOperateT1Today =
    isDev && ENABLE_TEST_VARIABLES && TEST_MARKET_CAN_OPERATE_T1_TODAY !== null
      ? TEST_MARKET_CAN_OPERATE_T1_TODAY
      : marketCanOperateT1Today;

  const isEnabledOperation = useMemo(() => {
    // If ENABLE_MARKET_ALWAYS is true, always return true (for development)
    if (isDev && ENABLE_MARKET_ALWAYS) {
      return true;
    }

    // If account is blocked, cannot operate (VHOME)
    if (
      accountSelected &&
      (symbolType === "FUNDS"
        ? !accountSelected?.isEnabled
        : !accountSelected.isEnabledOperate)
    ) {
      return false;
    }

    // If account is not allowed to operate, cannot operate (Clients accounts)
    if (
      (accountSelected && userCanOperateData?.allowed === false) ||
      isLoadingUserCanOperate ||
      !accountSelected
    ) {
      return false;
    }

    const now = dayjs().tz(ARGENTINA_TZ);
    const isWorkday = !isWeekend(now);

    // Apply test variable for market hours if enabled and in dev environment
    const withinHours =
      isDev && ENABLE_TEST_VARIABLES && TEST_IS_OUTSIDE_MARKET_HOURS !== null
        ? !TEST_IS_OUTSIDE_MARKET_HOURS
        : isWithinMarketHours(now);

    // If it's FUNDS, it's always open
    if (symbolType === "FUNDS") {
      return true;
    }

    // If it's REPO_OPERATION, cannot operate outside market hours or on holidays
    if (symbolType === "REPO_OPERATION") {
      return isWorkday && !finalIsHolidayToday && withinHours;
    }

    // For other operations: within market hours or if it's a holiday and marketCanOperateT1Today (always within market hours)
    if (isWorkday && withinHours) {
      // If it's a holiday, can only operate if marketCanOperateT1Today is true
      if (finalIsHolidayToday) {
        return finalMarketCanOperateT1Today;
      }
      // If it's not a holiday and within market hours, can operate
      return true;
    }

    return false;
  }, [
    symbolType,
    finalIsHolidayToday,
    finalMarketCanOperateT1Today,
    accountSelected,
    userCanOperateData,
    isLoadingUserCanOperate,
  ]);

  const canOperateT0 = useMemo(() => {
    // If T0 settlement is disabled, return false (only in dev environment)
    if (isDev && ENABLE_T0_SETTLEMENT !== null) {
      return ENABLE_T0_SETTLEMENT;
    }
    // Cannot operate T0 on holidays
    if (finalIsHolidayToday) {
      return false;
    }
    return true;
  }, [finalIsHolidayToday]);

  const canOperateT1 = useMemo(() => {
    // If T1 settlement is disabled, return false (only in dev environment)
    if (isDev && ENABLE_T1_SETTLEMENT !== null) {
      return ENABLE_T1_SETTLEMENT;
    }
    // Can operate T1 if it's a holiday and marketCanOperateT1Today is true
    if (finalIsHolidayToday && finalMarketCanOperateT1Today) {
      return true;
    }
    // Can operate T1 on non-holidays
    if (!finalIsHolidayToday) {
      return true;
    }
    return false;
  }, [finalIsHolidayToday, finalMarketCanOperateT1Today]);

  return {
    isEnabledOperation,
    canOperateT0,
    canOperateT1,
  };
};
